import { memo, useCallback, useEffect, useMemo, useState } from 'react';

// material-ui
import { Grid } from "@mui/material";

import { useAsyncNew } from 'hooks/useAsyncNew';
import NetworkAppsService from 'services/network-apps/NetworkApps';
import { OneChipInfoProvider, useOneChipInfoContext } from 'contexts/OneChipInfoContext';
import PlanFilters from 'modules/networksapps/PlanFilters';
import DataComponent from 'modules/networksapps/DataComponent';



import PlanDetailsContainer from 'modules/modals/PlanDetails/containers/PlanDetailsContainer';
import InfinityScrollWrapper from 'modules/modals/SelectPlan/components/InfinityScrollContainer';
import { useModal } from 'components/shared/modal';
import { useTranslation } from 'react-i18next';
import withEmptyData from 'hoc/witEmptyData';
import useDidMountEffect from 'hooks/useDidMountEffect';


const predefinedData = {
    coverageTypes: [
        { value: 'Single', label: 'Single' },
        { value: 'Multi', label: 'Multi' },
    ],
    credentialTypes: [
        {
            value: 'global',
            label: 'Global',
        },
        {
            value: 'local',
            label: 'Local',
        },
        {
            value: 'native',
            label: 'Native',
        },
    ],
    networkTypes: [
        {
            value: 'Public',
            label: 'Public Network',
        },
        {
            value: 'Private',
            label: 'Private Network',
        }],
    radioTechs: [
        { value: '5G', label: '5G' },
        { value: '3G', label: '3G' },
        { value: '2G', label: '2G' },
        { value: 'LTE', label: 'LTE' },
        { value: 'LTE_M', label: 'LTE_M' },
        { value: 'LTE_3G', label: 'LTE/3G' },
        { value: 'P_LTE', label: 'Private LTE' },
        { value: 'NB-IoT', label: 'NB-IoT' },
        { value: 'Other', label: 'Other' },
    ],
    services: [
        {
            value: 'meerkat',
            label: 'Meerkat',
        },
    ]
};


const SelectPlan = ({ onComplete = () => { } }) => {

    const modal = useModal({ stacked: false });

    const { t } = useTranslation();

    const { info } = useOneChipInfoContext();
    const infoData = info.data;

    const [filters, setFilters] = useState({
        limit: 10,
        offset: 0
    });

    const selectedList = useMemo(() => Object.entries(filters).reduce((acc, [name, item]) => {
        acc[name] = Array.isArray(item) ? item.map(i => i.value ? i.value : i) : item;
        return acc;
    }, {})
        , [filters]);

    const { execute, data, isSuccess, isLoading } = useAsyncNew(NetworkAppsService.getPlansList, { immediate: false });

    const [isNewData, setIsNewData] = useState(true);

    useEffect(() => {
        info.stop();
    }, []);

    useDidMountEffect(() => {
        if (info.isSuccess) {
            execute({
                ...selectedList,
                sort: 'name:asc',
                clientId: infoData?.esim?.clientId
            });
        }
    }, [selectedList])

    useEffect(() => {
        if (info.isSuccess) {
            setFilters(prev => ({
                ...prev,
                sku: infoData.esim.storeUnitUuid ? [{
                    label: infoData.esim.sku,
                    value: infoData.esim.storeUnitUuid
                }] : []
            }));
        }
    }, [info.isSuccess])

    const handleOpenPlanDetailsModal = (data) => {
        modal.openModal(PlanDetailsContainer,
            {
                data: {
                    ...data,
                    eid: infoData.esim.eid,
                },
                onComplete
            },
            {
                fullWidth: true,
                maxWidth: "md",
                title: [{ name: 'PlanSelectionModal', title: t('views.network_apps.select_apps.network_apps') }, { name: '', title: data.planName }],
                close: true,
                sx: {
                    '& .ModalContentWrapper-root': {
                        backgroundColor: '#F5F5F5'
                    }
                }
            });
    }

    const handleOnFilter = (filterName, values) => {
        const newFilters = { offset: 0 };
        setFilters(prev => ({ ...prev, [filterName]: values, ...newFilters }));
        setIsNewData(true);
    }

    const handleFetchMore = useCallback(() => {
        setIsNewData(false);
        setFilters(prev => ({ ...prev, offset: prev.offset + prev.limit }));
    }, [])

    return (
        <>

            <PlanFilters
                onFilter={handleOnFilter}
                isModal={true}
                filters={filters}
                selected={selectedList}
                filtersData={predefinedData}
            />

            <InfinityScrollWrapper
                data={data}
                isLoading={isLoading}
                onFetchMore={handleFetchMore}
                isNew={isNewData}
                isSuccess={isSuccess}
            >
                {(state) =>
                    <Grid container
                        columnSpacing={2}
                        alignItems={'right'}
                        rowSpacing={{ xs: 2 }}
                        sx={{ pt: 0, mt: 0 }}
                    >
                        {state.items.map((plan, index) => (
                            <Grid item xs={6} key={`grid-${plan._id}`} >
                                <DataComponent
                                    key={`tile-${plan._id}`}
                                    isRecommendation={false}
                                    item={plan}
                                    onClick={() => handleOpenPlanDetailsModal(plan)}
                                >
                                </DataComponent>
                            </Grid>
                        ))}
                    </Grid>
                }
            </InfinityScrollWrapper>
        </>
    )
}

const withOnechipInfoProvider = (Component) => {
    return (props) => (
        <OneChipInfoProvider>
            <Component {...props} />
        </OneChipInfoProvider>
    )
}

export default withOnechipInfoProvider(withEmptyData(SelectPlan));